// src/services/AuthService.js
import { DATA_SERVICE_BASE_URL } from './ApiService';

// Lazy load UsageEventsTracker to avoid circular dependency
let UsageEventsTrackerInstance = null;
function getUsageTracker() {
  if (UsageEventsTrackerInstance === null) {
    try {
      const module = require('./UsageEventsTracker');
      UsageEventsTrackerInstance = module.UsageEventsTracker || module.default;
    } catch (e) {
      console.warn('AuthService: Unable to load UsageEventsTracker', e);
      UsageEventsTrackerInstance = false; // Mark as failed to avoid retrying
    }
  }
  return UsageEventsTrackerInstance || null;
}

let isLoggingOut = false;
// Prevents concurrent 401 handlers from each independently clearing auth + redirecting
let isSessionExpired = false;
// Unified refresh mutex promise (single authority for refresh operations)
let activeRefreshPromise = null;

/** Check if the session has been marked as expired (used by UsageEventsApi to avoid cascade) */
export function getIsSessionExpired() {
  return isSessionExpired;
}

/**
 * Persist access/refresh tokens respecting remember_me_flag and expiration.
 * Centralized so both login and refresh paths use identical logic.
 */
function persistTokens({ accessToken, refreshToken, expiresIn }) {
  if (!accessToken) return;
  localStorage.setItem('access_token', accessToken);
  const remember = localStorage.getItem('remember_me_flag') === 'true';
  if (refreshToken) {
    if (remember) {
      localStorage.setItem('refresh_token', refreshToken);
      sessionStorage.removeItem('refresh_token');
    } else {
      sessionStorage.setItem('refresh_token', refreshToken);
      localStorage.removeItem('refresh_token');
    }
    // Timestamp helps guard against stale overwrites (future use)
    localStorage.setItem('refresh_token_issued_at', String(Date.now()));
  }
  if (expiresIn) {
    const expirationTime = Date.now() + (expiresIn * 1000) - 60000; // Refresh 1 min early
    localStorage.setItem('token_expires_at', String(expirationTime));
  }
}

/**
 * Internal refresh implementation (single authority). NEVER called directly outside this file.
 */
async function internalTokenRefresh() {
  const currentRefreshToken = getRefreshToken();
  if (!currentRefreshToken) {
    console.log('AuthService: No refresh token available for refresh');
    return null;
  }
  try {
    console.log('AuthService: Starting unified token refresh');
    const resp = await fetch(`${DATA_SERVICE_BASE_URL}/api/users/refresh-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'User-Agent': 'WebClient/1.0'
      },
      credentials: 'include',
      body: JSON.stringify({ refreshToken: currentRefreshToken })
    });
    if (!resp.ok) {
      const txt = await resp.text().catch(()=> '');
      console.warn('AuthService: Refresh failed', resp.status, txt);
      // Return null — let the caller (fetchWithAuth / checkTokenExpiration) decide
      // whether to clear auth and redirect. No redirect here to avoid double-clear.
      return null;
    }
    const data = await resp.json();
    if (!data.accessToken) {
      console.warn('AuthService: Refresh response missing accessToken');
      return null;
    }
    // Extract & persist
    const userId = extractUserIdFromToken(data.accessToken);
    if (userId) localStorage.setItem('user_id', String(userId));
    persistTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken, expiresIn: data.expiresIn || 1800 });
    console.log('AuthService: Unified token refresh succeeded');
    return data.accessToken;
  } catch (e) {
    console.error('AuthService: Unified token refresh exception', e);
    return null;
  }
}

/**
 * Public refresh function providing a mutex so concurrent callers share one refresh.
 */
export async function performTokenRefresh() {
  if (activeRefreshPromise) {
    return activeRefreshPromise; // Reuse in-flight refresh
  }
  activeRefreshPromise = (async () => {
    try {
      return await internalTokenRefresh();
    } finally {
      activeRefreshPromise = null;
    }
  })();
  return activeRefreshPromise;
}

/**
 * Strip query params and redact numeric/UUID path segments to keep
 * enough URL structure for debugging without leaking tokens or IDs.
 * e.g. "/api/users/42/subscription" → "/api/users/:id/subscription"
 */
function sanitizeUrlForLogging(rawUrl) {
  try {
    const u = new URL(rawUrl, window.location.origin);
    const sanitizedPath = u.pathname.replace(
      /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/:uuid'
    ).replace(
      /\/\d+/g, '/:id'
    );
    return sanitizedPath;
  } catch {
    return rawUrl.split('?')[0];
  }
}

/**
 * Custom fetch function that includes the Authorization header with a Bearer token.
 * It also handles 401 responses by logging the user out.
 * Tracks API performance for slow (>250ms) or failed requests.
 * @param {string} url - The URL to fetch. Should be a full URL.
 * @param {object} options - Fetch options (method, body, custom headers, etc.).
 * @param {boolean} _internalRetry (internal use only)
 * @returns {Promise<Response>} The fetch Response object.
 */
export const fetchWithAuth = async (url, options = {}, _internalRetry = false) => {
  const startTime = performance.now();
  
  // ✅ NEW: Proactive token expiration check - refresh if expiring within 60 seconds
  const isRefreshEndpoint = url.includes('/refresh-token');
  const isLogoutEndpoint = /\/logout\/\d+$/i.test(url);
  
  if (!isRefreshEndpoint && !isLogoutEndpoint && !_internalRetry) {
    const expiresAt = parseInt(localStorage.getItem('token_expires_at') || '0', 10);
    if (expiresAt > 0 && Date.now() + 60000 > expiresAt) {
      console.log('fetchWithAuth: Token expiring soon, refreshing proactively');
      await performTokenRefresh();
    }
  }
  
  const token = getAccessToken();
  const headers = {
    ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
    ...options.headers,
  };

  // Only attach Authorization if NOT the refresh endpoint
  if (!isRefreshEndpoint && token && !headers.Authorization) {
    headers.Authorization = `Bearer ${token}`;
  }
  headers['X-Client-Timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;

  let response = null;
  let requestSize = 0;
  let responseSize = 0;

  // Calculate request size
  if (options.body) {
    if (typeof options.body === 'string') {
      requestSize = new Blob([options.body]).size;
    } else if (options.body instanceof FormData) {
      // Approximate size for FormData (not exact)
      requestSize = 0; // Can't easily calculate FormData size
    }
  }

  try {
    response = await fetch(url, { ...options, headers, credentials: options.credentials || 'include' });

    // Get response size from Content-Length header
    const contentLength = response.headers.get('Content-Length');
    if (contentLength) {
      responseSize = parseInt(contentLength, 10);
    }

    // If unauthorized & not already retried & not currently logging out, attempt silent refresh
    if (response.status === 401 && !_internalRetry && !isLoggingOut && !isSessionExpired && !isRefreshEndpoint && !isLogoutEndpoint) {
      console.warn('fetchWithAuth: 401 received, attempting unified refresh');
      const newToken = await performTokenRefresh();
      if (newToken) {
        // Retry original request once with new token
        const retryHeaders = {
          ...headers,
          Authorization: `Bearer ${newToken}`
        };
        const retryResp = await fetch(url, { ...options, headers: retryHeaders });
        if (retryResp.status === 401 && !isSessionExpired) {
          console.warn('fetchWithAuth: Retry still 401 after refresh - session expired');
          isSessionExpired = true;
          clearAuthData({ preserveRememberMe: true });
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('auth:session-expired'));
          }
        }
        response = retryResp;
        
        // Update response size for retry
        const retryContentLength = retryResp.headers.get('Content-Length');
        if (retryContentLength) {
          responseSize = parseInt(retryContentLength, 10);
        }
      } else if (!isSessionExpired) {
        // No refresh token available or refresh failed - signal session expired
        console.warn('fetchWithAuth: No refresh token or refresh failed - session expired');
        isSessionExpired = true;
        clearAuthData({ preserveRememberMe: true });
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('auth:session-expired'));
        }
      }
    }

    // Track performance if slow (>250ms) or failed (4xx/5xx)
    // BUT: Exclude usage event endpoints to prevent infinite loop
    // Also exclude expected 404s from subscription endpoint (normal for free tier users)
    const duration = performance.now() - startTime;
    const isUsageEventEndpoint = url.includes('/api/stats/events');
    const isSubscriptionEndpoint404 = response.status === 404 && url.includes('/api/users/') && url.includes('/subscription');
    const shouldTrack = !isUsageEventEndpoint && !isSubscriptionEndpoint404 && (duration > 250 || response.status >= 400);
    
    if (shouldTrack) {
      const tracker = getUsageTracker();
      if (tracker && tracker.recordAPICall) {
        try {
          tracker.recordAPICall({
            url,
            method: options.method || 'GET',
            duration,
            status: response.status,
            requestSize,
            responseSize
          });
        } catch (trackError) {
          // Silent fail - don't break request flow
          console.warn('AuthService: Failed to track API call', trackError);
        }
      }
    }

    // Report 4xx/5xx API errors (excluding usage-event endpoints to avoid recursion,
    // and excluding expected 404s like free-tier subscription lookups)
    if (response.status >= 400 && !isUsageEventEndpoint && !isSubscriptionEndpoint404) {
      try {
        const { getErrorReportingService } = require('./errorReportingBridge');
        const ers = getErrorReportingService();
        if (ers) {
          const safeUrl = sanitizeUrlForLogging(url);
          ers.addBreadcrumb('api', `API ${response.status}: ${options.method || 'GET'} ${safeUrl}`, {
            status: response.status, method: options.method || 'GET',
          });
          if (response.status >= 500) {
            ers.captureMessage(
              `Server error ${response.status}: ${options.method || 'GET'} ${safeUrl}`,
              'error',
              { source: 'fetch_server_error', metadata: { status: response.status, method: options.method || 'GET', url: safeUrl } }
            );
          }
        }
      } catch (_) { /* silent */ }
    }

    // Optional: fetchWithAuth - add explicit non-auth status classification log
    if (response.status >= 500) {
      console.warn('fetchWithAuth: Server error', response.status, 'passing through (no logout)');
      return response;
    }

    // If server error (5xx), return response (caller decides) — no logout
    return response;
  } catch (error) {
    // Track network errors
    // BUT: Exclude usage event endpoints to prevent infinite loop
    const duration = performance.now() - startTime;
    const isUsageEventEndpoint = url.includes('/api/stats/events');
    const tracker = getUsageTracker();
    if (!isUsageEventEndpoint && tracker && tracker.recordAPICall) {
      try {
        tracker.recordAPICall({
          url,
          method: options.method || 'GET',
          duration,
          status: 0, // Network error
          requestSize,
          responseSize: 0
        });
      } catch (trackError) {
        console.warn('AuthService: Failed to track API error', trackError);
      }
    }
    
    console.warn(`fetchWithAuth: Network error for ${url}`, error);
    // Report network error to ErrorReportingService (skip during session expiry to avoid cascade)
    if (!isSessionExpired) {
      try {
        const { getErrorReportingService } = require('./errorReportingBridge');
        const ers = getErrorReportingService();
        if (ers) {
          ers.captureError(
            error instanceof Error ? error : new Error(String(error)),
            {
              source: 'fetch_network_error',
              severity: 'error',
              metadata: { url: sanitizeUrlForLogging(url), method: options.method || 'GET' },
            }
          );
        }
      } catch (_) { /* avoid recursion if tracker itself fails */ }
    }
    throw error;
  }
};

/**
 * Legacy function for backward compatibility
 * @deprecated Use getAccessToken() instead
 */
export const getAuthToken = () => {
  return getAccessToken();
};

/**
 * Handles authentication operations and token management
 */

/**
 * ✅ UPDATED: Store authentication data from JWT login response
 * @param {Object} authData - Authentication data from login response
 */
export function storeAuthData(authData) {
  console.log('AuthService: Storing auth data:', {
    hasToken: !!authData.accessToken,
    userId: authData.userId,
    sessionId: authData.sessionId,
    hasRefreshToken: !!authData.refreshToken,
    expiresIn: authData.expiresIn,
    rememberMe: authData.rememberMe
  });
  if (authData.rememberMe != null) {
    localStorage.setItem('remember_me_flag', String(!!authData.rememberMe));
  }
  if (authData.userId) {
    localStorage.setItem('user_id', authData.userId.toString());
  }
  if (authData.sessionId) {
    localStorage.setItem('session_id', authData.sessionId.toString());
  }
  persistTokens({ accessToken: authData.accessToken, refreshToken: authData.refreshToken, expiresIn: authData.expiresIn });
}

/**
 * Clears all authentication-related data from localStorage and sessionStorage.
 */
export function clearAuthData({ preserveRememberMe = false } = {}) {
  // Clear from localStorage
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user_id');
  localStorage.removeItem('session_id');
  localStorage.removeItem('user_details');
  localStorage.removeItem('token_expires_at');
  localStorage.removeItem('refresh_token_issued_at');
  if (!preserveRememberMe) {
    localStorage.removeItem('remember_me_flag');
  }
  
  // Clear from sessionStorage
  sessionStorage.removeItem('access_token');
  sessionStorage.removeItem('refresh_token');
  sessionStorage.removeItem('user_id');
  sessionStorage.removeItem('session_id');
  sessionStorage.removeItem('user_details');
  sessionStorage.removeItem('token_expires_at');
  sessionStorage.removeItem('refresh_token_issued_at');
  if (!preserveRememberMe) {
    sessionStorage.removeItem('remember_me_flag');
  }
}

// ===== Impersonation helpers =====

const IMPERSONATION_PREFIX = 'admin_original_';

/**
 * Save the current admin session data so it can be restored after impersonation ends.
 * Stored in sessionStorage so closing the tab auto-clears.
 */
export function saveAdminSession() {
  const keys = ['access_token', 'refresh_token', 'user_id', 'session_id',
                'token_expires_at', 'refresh_token_issued_at', 'remember_me_flag'];
  keys.forEach(k => {
    const v = localStorage.getItem(k) || sessionStorage.getItem(k);
    if (v) sessionStorage.setItem(IMPERSONATION_PREFIX + k, v);
  });
  sessionStorage.setItem(IMPERSONATION_PREFIX + 'active', 'true');
}

/**
 * Restore admin session from sessionStorage and clear impersonation markers.
 */
export function restoreAdminSession() {
  const keys = ['access_token', 'refresh_token', 'user_id', 'session_id',
                'token_expires_at', 'refresh_token_issued_at', 'remember_me_flag'];
  keys.forEach(k => {
    const v = sessionStorage.getItem(IMPERSONATION_PREFIX + k);
    if (v) {
      localStorage.setItem(k, v);
    }
    sessionStorage.removeItem(IMPERSONATION_PREFIX + k);
  });
  sessionStorage.removeItem(IMPERSONATION_PREFIX + 'active');
}

/**
 * Check if an impersonation session is currently active.
 */
export function isImpersonationActive() {
  return sessionStorage.getItem(IMPERSONATION_PREFIX + 'active') === 'true';
}

/**
 * Clear impersonation markers without restoring — for full logout during impersonation.
 */
export function clearImpersonationData() {
  const keys = ['access_token', 'refresh_token', 'user_id', 'session_id',
                'token_expires_at', 'refresh_token_issued_at', 'remember_me_flag', 'active'];
  keys.forEach(k => sessionStorage.removeItem(IMPERSONATION_PREFIX + k));
}

// ===== End impersonation helpers =====

/**
 * ✅ NEW: Extract user ID from JWT token payload
 * @param {string} token - JWT access token
 * @returns {number|null} - User ID from token or null if invalid
 */
function extractUserIdFromToken(token) {
  try {
    // JWT tokens have 3 parts separated by dots: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.warn('Invalid JWT token format');
      return null;
    }
    
    // Decode the payload (base64url)
    const payload = parts[1];
    // Add padding if needed for base64 decoding
    const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
    const decodedPayload = atob(paddedPayload.replace(/-/g, '+').replace(/_/g, '/'));
    const tokenData = JSON.parse(decodedPayload);
    
    console.log('Extracted token payload:', tokenData);
    
    // Return userId from token payload
    return tokenData.userId || tokenData.sub || null;
  } catch (error) {
    console.error('Error extracting user ID from token:', error);
    return null;
  }
}

// ✅ EXPORT: Make extractUserIdFromToken available for AuthContext
export { extractUserIdFromToken };

/**
 * Public user registration (self-signup)
 * Attempts to create a user account. If tokens are returned, it stores them.
 * Fallback: if no tokens are returned but response OK, caller should initiate login.
 * @param {{fullName:string,email:string,password:string}} param0
 * @returns {Promise<{success:boolean, autoLoggedIn:boolean, message?:string}>}
 */
/**
 * Public (temporary) self-signup using admin create-user endpoint.
 * Backend currently exposes only the authenticated POST /api/users route for creation.
 * Strategy:
 *  - Use existing access token if user somehow already authenticated (rare during signup)
 *  - Otherwise fall back to a provisioning bearer token provided via build/runtime env:
 *      REACT_APP_SIGNUP_PROVISIONING_TOKEN or SIGNUP_PROVISIONING_TOKEN (raw JWT string)
 *  - If no token available, fail fast with instructional message.
 *  - Endpoint does NOT return auth tokens → caller must perform a login afterwards.
 */
export async function registerUser({ fullName, email, password }) {
  try {
    const [firstName, ...rest] = (fullName || '').trim().split(/\s+/);
    const lastName = rest.join(' ') || firstName || 'User';
    const payload = { email, firstName: firstName || 'User', lastName: lastName || 'User', password };

    // Endpoint is now public (no Authorization header required)
    const resp = await fetch(`${DATA_SERVICE_BASE_URL}/api/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    if (!resp.ok) {
      const txt = await resp.text();
      return { success: false, autoLoggedIn: false, message: txt || `Registration failed (${resp.status})` };
    }

    let data = null;
    try { data = await resp.json(); } catch { /* ignore */ }
    // If backend evolves to return tokens, auto-login path still works.
    if (data && data.accessToken && data.refreshToken) {
      const userId = extractUserIdFromToken(data.accessToken);
      if (userId) {
        storeAuthData({ ...data, userId, rememberMe: true });
        return { success: true, autoLoggedIn: true };
      }
    }
    return { success: true, autoLoggedIn: false };
  } catch (e) {
    return { success: false, autoLoggedIn: false, message: e.message || 'Registration error' };
  }
}

/**
 * ✅ UPDATED: Login with email and password using JSON instead of FormData
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<object>} Login response with tokens and user info
 */
export async function login(email, password, rememberMe = false) {
  try {
    console.log('AuthService: Attempting login for:', email);
    
    // Clear stale tokens so no Authorization header contaminates login
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    sessionStorage.removeItem('refresh_token');
    isSessionExpired = false; // Reset flag on new login
    
    const loginData = {
      email: email,
      password: password,
      rememberMe: !!rememberMe
    };
    
    // Plain fetch (not fetchWithAuth) to avoid interceptor logic
    const response = await fetch(`${DATA_SERVICE_BASE_URL}/api/users/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json', // ✅ FIXED: Use JSON content type
        'User-Agent': 'WebClient/1.0',
        'X-Forwarded-For': '127.0.0.1'
      },
      body: JSON.stringify(loginData),
      credentials: 'include'
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.warn('AuthService: Login failed:', response.status, errorText);
      throw new Error('Invalid email or password');
    }
    
    const authData = await response.json();
    console.log('AuthService: Login successful, received data:', authData);
    
    const userId = extractUserIdFromToken(authData.accessToken);
    if (!userId) {
      throw new Error('Unable to extract user ID from access token');
    }
    
    const enhancedAuthData = {
      ...authData,
      userId: userId,
      rememberMe: !!rememberMe
    };
    
    if (!enhancedAuthData.accessToken || !enhancedAuthData.refreshToken || !enhancedAuthData.userId) {
      throw new Error('Invalid login response format');
    }
    
    storeAuthData(enhancedAuthData);
    
    return enhancedAuthData;
  } catch (error) {
    console.error('AuthService: Login error:', error);
    throw error;
  }
}

/**
 * ✅ UPDATED: Logout function using direct fetch
 */
export async function performLogout() {
  if (isLoggingOut) {
    return;
  }
  isLoggingOut = true;
  try {
    const userId = getUserId();
    const sessionId = getSessionId();
    console.log('AuthService: Performing logout for user:', userId, 'session:', sessionId);
    if (sessionId) {
      const token = getAccessToken();
      try {
        // Use plain fetch (NOT fetchWithAuth) to avoid intercept logic.
        await fetch(`${DATA_SERVICE_BASE_URL}/api/users/logout/${sessionId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        });
      } catch (e) {
        console.warn('AuthService: Logout request failed (continuing cleanup)', e);
      }
    } else if (userId) {
      // Fallback to userId route if sessionId absent
      try {
        await fetch(`${DATA_SERVICE_BASE_URL}/api/users/logout/${userId}`, { method: 'POST' });
      } catch (_) {}
    }
    clearAuthData();
    console.log('AuthService: Logout cleanup complete');
  } finally {
    isLoggingOut = false;
  }
}

/**
 * ✅ UPDATED: Request a password reset email using JSON
 * @param {string} email - User's email address
 * @returns {Promise<Response>} Response indicating if reset email was sent
 */
export async function requestPasswordReset(email) {
  try {
    // ✅ FIXED: Use JSON instead of query parameters if backend expects JSON
    const resetData = { email: email };
    
    const response = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/users/request-reset`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(resetData)
    });
    return response;
  } catch (error) {
    console.error('Password reset request error:', error);
    throw error;
  }
}

/**
 * ✅ UPDATED: Reset password with token using JSON
 * @param {string} token - Reset token from email
 * @param {string} newPassword - New password
 * @returns {Promise<Response>} Response indicating if password was reset
 */
export async function resetPassword(token, newPassword) {
  try {
    // ✅ FIXED: Use JSON instead of query parameters if backend expects JSON
    const resetData = { 
      token: token, 
      newPassword: newPassword 
    };
    
    const response = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/users/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(resetData)
    });
    return response;
  } catch (error) {
    console.error('Password reset error:', error);
    throw error;
  }
}

/**
 * Reset password by email (Admin only)
 * @param {string} email - User email
 * @param {string} newPassword - New password
 * @returns {Promise<Response>} Password reset response
 */
export async function resetPasswordByEmail(email, newPassword) {
  try {
    const queryParams = new URLSearchParams({ email, newPassword }).toString();
    const response = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/users/reset-password-email?${queryParams}`, {
      method: 'POST',
    });
    return response;
  } catch (error) {
    console.error('Password reset by email error:', error);
    throw error;
  }
}

// === Helper accessors re-added (expected by AuthContext & APIs) ===
export function getAccessToken() {
  return localStorage.getItem('access_token') || null;
}
export function getRefreshToken() {
  const remember = localStorage.getItem('remember_me_flag') === 'true';
  if (remember) {
    return localStorage.getItem('refresh_token') || sessionStorage.getItem('refresh_token') || null;
  }
  return sessionStorage.getItem('refresh_token') || localStorage.getItem('refresh_token') || null;
}
export function getUserId() {
  const v = localStorage.getItem('user_id');
  return v ? Number(v) : null;
}
export function getSessionId() {
  const v = localStorage.getItem('session_id');
  return v || null;
}

// Fetch user details (fallback if validate-token not used)
export async function fetchUserDetails() {
  const userId = getUserId();
  const token = getAccessToken();
  if (!userId || !token) return null;
  try {
    const resp = await fetch(`${DATA_SERVICE_BASE_URL}/api/users/${userId}`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    if (!resp.ok) {
      console.warn('fetchUserDetails: non-OK', resp.status);
      return null;
    }
    return await resp.json();
  } catch (e) {
    console.warn('fetchUserDetails: error', e);
    return null;
  }
}
// === End helper accessors ===
